"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  classRange,
  convertUkMark,
  summariseUk,
  UK_CLASSES,
  UK_US_SCALES,
  type UkClass,
  type UkUsScale,
  type UkUsSummary,
  ukUsBands,
} from "@/lib/tool-kit/intl-grades/uk-us-gpa";

/** English strings. A translated content file overrides these through `ui`. */
export const UK_TO_US_GPA_CONVERTER_STRINGS = {
  scaleLabel: "Published conversion table",
  scales: {
    andrews: "Andrews University — degree classification averages",
    ncsu: "NC State University study abroad table",
    uconn: "University of Connecticut table (Warwick marks, finer steps)",
  },
  scaleHint:
    "There is no official UK-to-US conversion. These are three published tables, and the receiving university or evaluator decides which applies.",
  classHeading: "From a degree class",
  classLabel: "UK degree class",
  classes: {
    first: "First class honours (70 and above)",
    "upper-second": "Upper second, 2:1 (60–69)",
    "lower-second": "Lower second, 2:2 (50–59)",
    third: "Third class honours (40–49)",
    below: "Below honours",
  },
  classLetters: "US letter grade",
  classPoints: "US GPA",
  rangeText: "{low} to {high}",
  classNote:
    "A class spans a range of marks, so most tables give a range of letters. Use your average mark below for a single figure.",
  marksHeading: "From your module marks",
  markLabel: "Mark % for module {n}",
  creditsLabel: "Credits for module {n}",
  addModule: "Add a module",
  removeModule: "Remove",
  removeModuleAria: "Remove module {n}",
  results: "Result",
  average: "Credit-weighted average",
  gpa: "US GPA (4.0 scale)",
  letter: "US letter for the average",
  ukClass: "UK class for the average",
  tableHeading: "Module by module",
  colModule: "Module",
  colMark: "Mark %",
  colLetter: "US letter",
  colPoints: "Grade points",
  colCredits: "Credits",
  moduleName: "Module {n}",
  skipped: "Skipped (not a mark from 0 to 100, or no credits): {rows}",
  empty: "Enter at least one mark from 0 to 100 with credits above zero.",
  bandsHeading: "The table in use",
  colFrom: "Mark from",
  bandFrom: "{min}",
  weighting:
    "This averages module marks only. Many UK degrees weight the final years more heavily, so for your classification use the degree classification calculator first.",
  caveat:
    "The result is an estimate. US admissions offices and credential evaluators apply their own tables, and many US universities do not convert a degree class at all.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "UK average {average}% = GPA {gpa} ({letter}), {ukClass}",
} as const;

type Strings = typeof UK_TO_US_GPA_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

interface Row {
  id: number;
  mark: string;
  credits: string;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, mark: "72", credits: "30" },
  { id: 2, mark: "68", credits: "30" },
  { id: 3, mark: "64", credits: "30" },
  { id: 4, mark: "52", credits: "15" },
  { id: 5, mark: "46", credits: "15" },
];

const TWO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

function ModuleRow({
  row,
  index,
  strings,
  fmt,
  onChange,
  onRemove,
}: {
  row: Row;
  index: number;
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  return (
    <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
      <TextField
        id={`ukus-mark-${row.id}`}
        label={t(strings.markLabel, { n })}
        numeric
        onChange={(mark) => onChange({ ...row, mark })}
        value={row.mark}
      />
      <TextField
        id={`ukus-credits-${row.id}`}
        label={t(strings.creditsLabel, { n })}
        numeric
        onChange={(credits) => onChange({ ...row, credits })}
        value={row.credits}
      />
      <button
        aria-label={t(strings.removeModuleAria, { n })}
        className={BUTTON_SECONDARY}
        onClick={onRemove}
        type="button"
      >
        {strings.removeModule}
      </button>
    </div>
  );
}

function ClassPanel({
  scale,
  ukClass,
  onClass,
  strings,
  fmt,
}: {
  scale: UkUsScale;
  ukClass: Exclude<UkClass, "below">;
  onClass: (value: Exclude<UkClass, "below">) => void;
  strings: Strings;
  fmt: Format;
}) {
  const range = classRange(ukClass, scale);
  const range2 = (a: string, b: string) =>
    a === b ? a : t(strings.rangeText, { low: a, high: b });
  const points = (value: number) =>
    fmt(value, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  return (
    <section className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.classHeading}</h3>
      <SelectField
        id="ukus-class"
        label={strings.classLabel}
        onChange={onClass}
        options={UK_CLASSES.map((value) => ({
          value: value as Exclude<UkClass, "below">,
          label: strings.classes[value],
        }))}
        value={ukClass}
      />
      <div aria-live="polite" className="space-y-2">
        {range ? (
          <StatGrid>
            <StatCard
              emphasis
              label={strings.classLetters}
              value={range2(range.lowest.letter, range.highest.letter)}
            />
            <StatCard
              label={strings.classPoints}
              value={range2(
                points(range.lowest.points),
                points(range.highest.points)
              )}
            />
          </StatGrid>
        ) : null}
        <p className="text-muted-foreground text-sm">{strings.classNote}</p>
      </div>
    </section>
  );
}

function ResultsSection({
  summary,
  tableRows,
  skipped,
  scale,
  strings,
  fmt,
}: {
  summary: UkUsSummary | null;
  tableRows: string[][];
  skipped: string[];
  scale: UkUsScale;
  strings: Strings;
  fmt: Format;
}) {
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {summary === null ? (
        <Notice>{strings.empty}</Notice>
      ) : (
        <>
          <StatGrid>
            <StatCard
              label={strings.average}
              value={fmt(summary.average, { maximumFractionDigits: 2 })}
            />
            <StatCard
              emphasis
              label={strings.gpa}
              value={fmt(summary.gpa, TWO_DECIMALS)}
            />
            <StatCard label={strings.letter} value={summary.band.letter} />
            <StatCard
              label={strings.ukClass}
              value={strings.classes[summary.ukClass]}
            />
          </StatGrid>
          {skipped.length > 0 ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.skipped, { rows: skipped.join(", ") })}
            </p>
          ) : null}
          <DataTable
            caption={strings.tableHeading}
            head={[
              strings.colModule,
              strings.colMark,
              strings.colLetter,
              strings.colPoints,
              strings.colCredits,
            ]}
            rows={tableRows}
          />
          <DataTable
            caption={strings.bandsHeading}
            head={[strings.colFrom, strings.colLetter, strings.colPoints]}
            rows={ukUsBands(scale).map((band) => [
              fmt(band.min),
              band.letter,
              fmt(band.points, TWO_DECIMALS),
            ])}
          />
          <p className="text-muted-foreground text-sm">{strings.weighting}</p>
          <p className="text-muted-foreground text-sm">{strings.caveat}</p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={t(strings.summary, {
              average: fmt(summary.average, { maximumFractionDigits: 2 }),
              gpa: fmt(summary.gpa, TWO_DECIMALS),
              letter: summary.band.letter,
              ukClass: strings.classes[summary.ukClass],
            })}
          />
        </>
      )}
    </section>
  );
}

export default function UkToUsGpaConverterWidget() {
  const strings = useToolUi(UK_TO_US_GPA_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [scale, setScale] = useState<UkUsScale>("ncsu");
  const [ukClass, setUkClass] =
    useState<Exclude<UkClass, "below">>("upper-second");
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(6);

  const parsed = rows.map((row) => ({
    mark: parseNumberInput(row.mark),
    credits: parseNumberInput(row.credits),
  }));
  const modules = parsed.flatMap(({ mark, credits }) =>
    mark === null || credits === null ? [] : [{ mark, credits }]
  );
  const summary = summariseUk(modules, scale);
  const skipped = parsed.flatMap(({ mark, credits }, index) =>
    mark !== null &&
    credits !== null &&
    credits > 0 &&
    convertUkMark(mark, scale)
      ? []
      : [fmt(index + 1)]
  );
  const tableRows = parsed.flatMap(({ mark, credits }, index) => {
    const band = mark === null ? null : convertUkMark(mark, scale);
    if (mark === null || credits === null || band === null || credits <= 0) {
      return [];
    }
    return [
      [
        t(strings.moduleName, { n: fmt(index + 1) }),
        fmt(mark, { maximumFractionDigits: 2 }),
        band.letter,
        fmt(band.points, TWO_DECIMALS),
        fmt(credits, { maximumFractionDigits: 2 }),
      ],
    ];
  });

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <SelectField
          hint={strings.scaleHint}
          id="ukus-scale"
          label={strings.scaleLabel}
          onChange={setScale}
          options={UK_US_SCALES.map((value) => ({
            value,
            label: strings.scales[value],
          }))}
          value={scale}
        />
      </div>
      <ClassPanel
        fmt={fmt}
        onClass={setUkClass}
        scale={scale}
        strings={strings}
        ukClass={ukClass}
      />
      <div className={`${CARD} space-y-5`}>
        <h3 className={WIDGET_H3}>{strings.marksHeading}</h3>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <ModuleRow
              fmt={fmt}
              index={index}
              key={row.id}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
            />
          ))}
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setRows((current) => [
              ...current,
              { id: nextId, mark: "", credits: "15" },
            ]);
            setNextId((value) => value + 1);
          }}
          type="button"
        >
          {strings.addModule}
        </button>
      </div>
      <ResultsSection
        fmt={fmt}
        scale={scale}
        skipped={skipped}
        strings={strings}
        summary={summary}
        tableRows={tableRows}
      />
    </div>
  );
}
